<script setup lang="ts">
import { ElMessage } from 'element-plus'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import {
  useConversationsQuery,
  useDeleteConversationMutation,
  useUpdateConversationTitleMutation,
} from '@/entities/ai'
import { CButton, CDialog } from '@/shared/ui'

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()

const route = useRoute()
const router = useRouter()
const page = ref<number>(1)
const search = ref('')
const isDeleteDialogVisible = ref(false)
const isEditDialogVisible = ref(false)
const deleteDialogMessage = ref('')
const editDialogTitle = ref('')
const conversationToDelete = ref<{ id: number, title: string } | null>(null)
const conversationToEdit = ref<{ id: number, title: string } | null>(null)

const { data, refetch } = useConversationsQuery(page)
const { mutateAsync: deleteConversation, isPending: isDeleting }
  = useDeleteConversationMutation()
const { mutateAsync: updateConversationTitle, isPending: isUpdating }
  = useUpdateConversationTitleMutation()

// Remove the mock update mutation
// const isUpdating = ref(false)

watch(page, () => refetch())

const conversations = computed(() => data?.value?.results ?? [])

const filteredConversations = computed(() => {
  if (!search.value) 
    return conversations.value
  const q = search.value.toLowerCase()
  return conversations.value.filter(c => c.title.toLowerCase().includes(q))
})

// Helper function to truncate long titles for display
function truncateTitle(title: string, maxLength: number = 50) {
  if (title.length <= maxLength) 
    return title
  return `${title.slice(0, maxLength)}...`
}

function goTo(id: number) {
  router.push(`/ai/${id}`)
  emit('close')
}

async function onCommand(item: { id: number, title: string }, cmd: string) {
  if (cmd === 'rename') {
    conversationToEdit.value = item
    editDialogTitle.value = item.title
    isEditDialogVisible.value = true
  }
  else if (cmd === 'share') {
    // Placeholder for future share action
  }
  else if (cmd === 'delete') {
    conversationToDelete.value = item
    deleteDialogMessage.value
      = String(item.id) === route.params.id
        ? t('delete_current_conversation_confirm', {
            title: truncateTitle(item.title),
          })
        : t('delete_conversation_confirm', { title: truncateTitle(item.title) })
    isDeleteDialogVisible.value = true
  }
}

async function confirmUpdate() {
  if (!conversationToEdit.value || !editDialogTitle.value.trim()) 
    return

  try {
    const item = conversationToEdit.value
    const newTitle = editDialogTitle.value.trim()

    // Use actual API call to update conversation title
    await updateConversationTitle({ id: item.id, title: newTitle })

    // Show success message
    ElMessage.success(t('conversation_renamed_success'))

    // Refetch conversations to update the sidebar
    await refetch()

    // Close dialog and reset
    isEditDialogVisible.value = false
    conversationToEdit.value = null
    editDialogTitle.value = ''
  }
  catch (error) {
    ElMessage.error(t('conversation_rename_error'))
    console.error('Update conversation title error:', error)
  }
}

async function confirmDelete() {
  if (!conversationToDelete.value) 
    return

  try {
    const item = conversationToDelete.value
    const isCurrentConversation = String(item.id) === route.params.id

    await deleteConversation(item.id)

    // Show success message
    ElMessage.success(t('conversation_deleted_success'))

    // Refetch conversations to update the sidebar
    await refetch()

    // If we're currently viewing the deleted conversation, redirect to AI page
    if (isCurrentConversation) {
      router.push('/ai')
    }

    // Close dialog and reset
    isDeleteDialogVisible.value = false
    conversationToDelete.value = null
    deleteDialogMessage.value = ''
  }
  catch (error) {
    ElMessage.error(t('conversation_delete_error'))
    console.error('Delete conversation error:', error)
  }
}

function onNewConversation() {
  router.push('/ai')
  emit('close')
}
</script>

<template>
  <aside class="w-72 min-w-64 max-w-80 h-full border-r bg-white rounded-tl-2xl">
    <div class="p-3 flex gap-2">
      <el-input
        v-model="search"
        size="small"
        :placeholder="$t('ai_search_placeholder')"
        clearable
        class="w-full text-sm h-8"
      >
        <template #prefix>
          <i class="icon-search text-sm !ml-2" />
        </template>
      </el-input>
      <CButton
        size="small"
        type="default"
        class="cursor-pointer flex items-center justify-center !h-8 !w-8 !border-none"
        @click="onNewConversation"
      >
        <i class="icon-pencil-edit-01 text-xl text-text-regular" />
      </CButton>
    </div>

    <div
      class="px-3 space-y-2 overflow-y-auto"
      style="max-height: calc(100% - 72px)"
    >
      <!-- Conversations list -->
      <template v-if="filteredConversations.length > 0">
        <div
          v-for="item in filteredConversations"
          :key="item.id"
          class="group flex items-center justify-between border hover:bg-gray-100 transition-200 rounded-lg px-2 !h-8 cursor-pointer"
          :class="{ 'bg-gray-100': String(item.id) === route.params.id }"
        >
          <span
            class="text-[13px] font-medium leading-[22px] text-text-regular flex-1 cursor-pointer"
            @click="goTo(item.id)"
          >
            {{ item.title.slice(0, 25) }}...
          </span>

          <div @click.stop>
            <el-dropdown
              trigger="click"
              :disabled="isDeleting || isUpdating"
              :teleported="false"
              @command="(cmd: string) => onCommand(item, cmd)"
            >
              <span
                class="el-dropdown-link px-2 py-1 rounded-md cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                :class="{ '!opacity-50': isDeleting || isUpdating }"
              >
                <span class="text-lg font-bold text-gray-500 hover:text-black">⋮</span>
              </span>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="rename">
                    <i class="icon-pencil-edit-01 text-md" /> {{ $t("rename") }}
                  </el-dropdown-item>
                  <el-dropdown-item command="share">
                    <i class="icon-share text-md" /> {{ $t("share") }}
                  </el-dropdown-item>
                  <el-dropdown-item command="delete" :disabled="isDeleting">
                    <i class="icon-delete text-md" />
                    {{ isDeleting ? $t("deleting") : $t("delete") }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </div>
      </template>

      <!-- Empty state -->
      <template v-else>
        <div
          class="flex flex-col items-center justify-center py-8 px-4 text-center"
        >
          <i class="icon-message-text-circle-01 text-4xl text-gray-300 mb-3" />
          <p class="text-sm text-gray-500 mb-2">
            {{ $t("no_conversations") }}
          </p>
          <p class="text-xs text-gray-400">
            {{ $t("create_new_conversation_hint") }}
          </p>
        </div>
      </template>
    </div>

    <!-- Edit Dialog -->
    <CDialog
      v-model="isEditDialogVisible"
      :title="$t('rename_conversation')"
      class="max-w-[480px]"
    >
      <template #header>
        <h3 class="text-lg font-medium">
          {{ $t("rename_conversation") }}
        </h3>
      </template>

      <div class="space-y-3">
        <label class="block text-sm font-medium text-gray-700">
          {{ $t("conversation_title_label") }}
        </label>
        <el-input
          v-model="editDialogTitle"
          :placeholder="$t('enter_new_conversation_title')"
          :disabled="isUpdating"
          @keyup.enter="confirmUpdate"
        />
      </div>

      <template #footer>
        <div class="flex justify-end gap-3">
          <CButton
            type="default"
            :disabled="isUpdating"
            @click="isEditDialogVisible = false"
          >
            {{ $t("cancel") }}
          </CButton>
          <CButton
            type="primary"
            :loading="isUpdating"
            :disabled="isUpdating || !editDialogTitle.trim()"
            @click="confirmUpdate"
          >
            {{ $t("update") }}
          </CButton>
        </div>
      </template>
    </CDialog>

    <!-- Delete Dialog -->
    <CDialog
      v-model="isDeleteDialogVisible"
      :title="$t('delete_conversation')"
      class="max-w-[480px]"
    >
      <template #header>
        <h3 class="text-lg font-medium">
          {{ $t("delete_conversation") }}
        </h3>
      </template>

      <p class="text-gray-600">
        {{ deleteDialogMessage }}
      </p>

      <template #footer>
        <div class="flex justify-end gap-3">
          <CButton
            type="default"
            :disabled="isDeleting"
            @click="isDeleteDialogVisible = false"
          >
            {{ $t("cancel") }}
          </CButton>
          <CButton
            type="danger"
            :loading="isDeleting"
            :disabled="isDeleting"
            @click="confirmDelete"
          >
            {{ $t("delete") }}
          </CButton>
        </div>
      </template>
    </CDialog>
  </aside>
</template>

<style scoped>
.el-dropdown-link {
  pointer-events: auto;
  z-index: 10;
  display: inline-block;
}

/* Ensure dropdown menu appears above other elements */
:deep(.el-dropdown-menu) {
  z-index: 9999;
}
</style>
